import { cn } from "@/lib/utils";
import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef } from "react";

const textVariants = cva("", {
  variants: {
    variant: {
      /* --------------------------------- Heading --------------------------------- */
      heading_h1:
        "text-[48px] text-center leading-[150%] font-semibold text-text-primary",
      heading_h2:
        "text-[40px] text-center leading-[150%] font-semibold text-text-primary",
      heading_h3:
        "text-[32px] text-center leading-[150%] font-semibold text-text-primary",
      heading_h4:
        "text-[24px] text-center leading-[150%] font-bold text-text-primary",
      heading_h5:
        "text-[18px] text-center leading-[150%] font-medium text-text-primary",
      heading_h6:
        "text-[16px] text-center leading-[150%] font-semibold text-text-primary",

      /* --------------------------------- Paragraph --------------------------------- */
      paragraph_p1: "text-sm font-normal text-text-primary leading-[22px]",
      paragraph_p2: "text-sm font-normal text-text-primary leading-[22px]",
      paragraph_p3:
        "text-[16px] font-normal text-text-primary leading-[28px] letter-[-0.5px]",
      paragraph_p4:
        "text-[14px] font-normal text-text-primary leading-[22px] tracking-[0.5px]",
      paragraph_p5: "text-sm font-normal text-text-primary leading-[22px]",
      paragraph_p6: "text-sm font-normal text-text-primary leading-[22px]",
      /* --------------------------------- Charts --------------------------------- */
      chartX: "text-xs font-normal text-text-primary",
      chartY: "text-xs font-normal text-text-primary",
      chartLegend: "text-xs font-medium text-text-primary",
      chartValue: "text-[11px] leading-3 font-medium text-text-primary",
      chartTitle: "text-base leading-5 font-semibold text-text-primary",

      /* -------------------------------- Nav Text -------------------------------- */
      leftNavText: "text-base font-semibold text-text-primary",
      textCategory: "text-xs tracking-[4%] text-text-primary font-bold",
      navText: "text-base text-text-primary font-medium",

      /* ------------------------------- Table Text ------------------------------- */
      tableContentText: "text-sm font-medium text-text-primary",
      tableContentUnderlined: "text-sm font-medium text-text-primary underline",
      tableColumnNameText:
        "text-[13px] font-medium tracking-[3%] text-text-primary uppercase",

      /* -------------------------------- Card Text ------------------------------- */
      cardTitle: "text-sm font-medium text-text-primary",
      cardSubtitle: "text-sm font-medium text-text-primary",
      cardCompany: "text-lg font-semibold text-text-primary",
      cardValue: "text-text-primary text-[26px] leading-[31px] font-semibold",

      /* --------------------------------- Filter Text --------------------------------- */
      filterTitle: "text-base font-bold text-text-secondary",

      /* ------------------------------ General Text ------------------------------ */
      normalText: "text-sm font-normal text-text-primary leading-[22px]",
      boldText: "text-sm font-semibold text-text-primary leading-[16px]",

      buttonText: "text-base text-text-primary font-medium",
      pageTitle: "text-2xl font-semibold text-text-primary leading-[29px]",
    },
  },
  defaultVariants: {
    variant: "normalText",
  },
});

export interface TextProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof textVariants> {}

const Text = forwardRef<HTMLButtonElement, TextProps>(
  ({ className, variant, ...props }, ref) => {
    return (
      <span
        className={cn(textVariants({ variant, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Text.displayName = "Text";

export default Text;
